export type ProjectImage = {
  src: string;
  width: number;
  height: number;
  alt: string;
  caption: string;
};

export type Project = {
  slug: string;
  number: string;
  title: string;
  category: string;
  type: string;
  status: string;
  description: string;
  role: string;
  technologies: string[];
  year: string;
  liveUrl: string | null;
  repositoryUrl: string | null;
  cover?: ProjectImage;
  views?: ProjectImage[];
  overview: string;
  challenge: string;
  goals: string[];
  audience: string;
  responsibilities: string[];
  direction: string;
  interaction: string;
  technical: string;
  responsive: string;
  accessibility: string;
  performance: string;
  solution: string;
  outcome: string;
  lessons: string;
  nextSteps: string;
};

export const projects: Project[] = [
  {
    slug: "aero-01",
    number: "01",
    title: "AERO/01",
    category: "Interactive 3D experience",
    type: "Self-initiated · fictional product",
    status: "Live concept",
    year: "2026",
    description:
      "An interactive website for a fictional running shoe, built around a model you can turn, inspect, and take apart.",
    role: "Design & development",
    technologies: ["React", "TypeScript", "Three.js", "Blender"],
    liveUrl: "https://goldenrod-parrot-699547.hostingersite.com/",
    repositoryUrl: null,
    cover: {
      src: "/images/projects/aero-live-home.webp",
      width: 1440,
      height: 1000,
      alt: "The live AERO/01 homepage, with oversized type, lime accents, and its interactive running shoe",
      caption: "Live website / AERO/01 product experience",
    },
    views: [
      {
        src: "/images/projects/aero-live-performance.webp",
        width: 1440,
        height: 1231,
        alt: "AERO/01’s performance section with selectable weight, drop, and return metrics and a component-weight chart",
        caption: "01 / Live website · The performance data study",
      },
      {
        src: "/images/projects/aero-live-colorways.webp",
        width: 1440,
        height: 1400,
        alt: "The live AERO/01 product selector showing studio views, five colorways, sizes, and sample specifications",
        caption: "02 / Live website · Colorways and product details",
      },
    ],
    overview:
      "AERO/01 started with two interests of mine: running and building for the web. I wanted to make a shoe launch page where you could look closely at the product, from the full model down to the six layers inside it.",
    challenge:
      "The shoe needed to be interesting to explore without making the rest of the page difficult to use. That meant planning for phones, keyboards, and browsers that could not load the 3D scene.",
    goals: [
      "Put product exploration at the center of the story.",
      "Connect the full shoe to its six engineering layers.",
      "Preserve the story when motion or 3D is unavailable.",
    ],
    audience:
      "I had runners and people interested in product design in mind. This was a self-initiated concept, so I did not have customer research to work from.",
    responsibilities: [
      "Creative direction & interface design",
      "Responsive front-end development",
      "3D integration & material direction",
      "Motion, accessibility & rendering decisions",
    ],
    direction:
      "I used a dark background and bright green accents for the launch page, with warm studio images when the shoe needed a quieter setting. Large type introduces the product; the close-up views explain its construction.",
    interaction:
      "You can rotate the shoe, switch colorways, scroll through an exploded view, and see it on different surfaces. The reservation flow is a demo. Here in the portfolio, the model loads only when you choose to explore it.",
    technical:
      "React and TypeScript handle the interface. The source uses React Three Fiber and a Draco-compressed Blender GLB. Six independent groups represent the upper, sockliner, foam cradle, carbon plate, foam carrier, and outsole. Local textures and renders also come from the same model.",
    responsive:
      "The original project reframes the product by viewport, uses native vertical scrolling, and provides explicit alternate-view controls. This portfolio fits the same asset to its preview viewport and keeps a studio poster available.",
    accessibility:
      "The source provides reduced-motion and static presentations, keyboard product rotation, labeled controls, and dialog focus management. The portfolio’s viewer adds named rotate and reset buttons; all explanatory content remains in HTML.",
    performance:
      "The compressed model is about 5.4 MB. It downloads after you choose “Explore in 3D,” and the viewer releases its resources when it leaves the screen. I also cap rendering resolution to reduce the load on smaller devices.",
    solution:
      "Keeping the shoe consistent across the interactive scene, exploded view, and still images took some care. I used the same Blender model for all of them so the shape and materials would match.",
    outcome:
      "The concept is live, with an interactive model and still-image alternatives. The shoe is fictional; the site does not take real orders or reservations.",
    lessons:
      "I learned to work on the model and the page together. A model can look great in Blender and still need different framing, lighting, or detail to work well in a browser.",
    nextSteps:
      "Move the demo to a dedicated domain, test it on more physical phones, and revisit the shoe model’s proportions and detail.",
  },
  {
    slug: "midwest-regal-firm",
    number: "02",
    title: "Midwest Regal Firm",
    category: "Service-business website",
    type: "Business website",
    status: "Live website",
    year: "2026",
    description:
      "A website that explains doorstep trash collection and helps apartment property managers request a quote.",
    role: "Design & front-end development",
    technologies: ["HTML", "CSS", "JavaScript", "Formspree"],
    liveUrl: "https://midwestregalfirm.com/",
    repositoryUrl: null,
    cover: {
      src: "/images/projects/midwest-regal-firm.webp",
      width: 1440,
      height: 1000,
      alt: "Midwest Regal Firm’s live homepage, with a navy and gold doorstep-service headline over apartment imagery",
      caption: "Live website / Homepage",
    },
    views: [
      {
        src: "/images/projects/midwest-regal-firm.webp",
        width: 1440,
        height: 1000,
        alt: "The Midwest Regal Firm homepage and its Get a Free Quote action",
        caption: "01 / The live homepage",
      },
      {
        src: "/images/projects/midwest-property.webp",
        width: 1200,
        height: 900,
        alt: "A trash-valet service cart in an apartment hallway, from the project’s image library",
        caption: "02 / Service imagery from the project",
      },
    ],
    overview:
      "Midwest Regal Firm needed a website for its doorstep trash-valet service. I brought the service steps, benefits, common questions, and quote request together on one page so property managers could get the information they needed.",
    challenge:
      "A property manager should be able to understand the service quickly. I needed to explain how collection works and make the quote form easy to find without repeating a sales pitch in every section.",
    goals: [
      "Explain the service before asking visitors to get in touch.",
      "Keep the quote request easy to find throughout the page.",
      "Give the business a consistent identity across desktop and mobile.",
    ],
    audience:
      "Apartment property managers and multifamily teams looking into a doorstep collection service for their residents.",
    responsibilities: [
      "Page structure and visual direction",
      "Responsive HTML, CSS, and JavaScript",
      "Comparison slider, FAQ, and quote-form interface",
    ],
    direction:
      "Navy, gold, and a serif headline give the business a consistent look. I used apartment imagery to show where the service fits, then broke up the longer page with clear headings and smaller sections.",
    interaction:
      "Visitors can move through the service steps, drag a before-and-after comparison, open FAQ answers, and jump straight to the quote form. The mobile menu closes after choosing a section so it stays out of the way of the page.",
    technical:
      "The site uses plain HTML, CSS, and JavaScript with no application build step. JavaScript handles the small interactive pieces; the quote form posts to Formspree and has inline success and error feedback.",
    responsive:
      "The wide navigation becomes a mobile menu, content grids stack, and the comparison table can scroll horizontally. The image comparison supports both mouse and touch input.",
    accessibility:
      "The comparison slider supports arrow keys and exposes its current value. FAQ buttons announce whether they are expanded, and Escape closes the mobile menu and returns focus to its trigger.",
    performance:
      "A static page keeps the deployment straightforward and avoids an application runtime. Scroll work is scheduled with requestAnimationFrame, and the hero parallax is disabled on small screens and for reduced motion.",
    solution:
      "I organized the page around three questions: what is included, how does it work, and how do I get a quote? That helped me decide what to keep near the top and what could sit further down.",
    outcome:
      "The website is live at midwestregalfirm.com. Visitors can read about the service, compare options, check the FAQ, and send a quote request.",
    lessons:
      "Writing down the questions a visitor might ask made the layout easier to plan. The page improved when I focused on answering them in a useful order.",
    nextSteps:
      "Test the complete inquiry flow on more phones, reduce image weight, and add FAQ answers as the business hears new questions.",
  },
  {
    slug: "templatevaults",
    number: "03",
    title: "TemplateVaults",
    category: "Website template storefront",
    type: "Digital product platform",
    status: "Live website",
    year: "2026",
    description:
      "A storefront for website templates, with category filters, live demos, and downloads after checkout.",
    role: "Design & full-stack development",
    technologies: ["Next.js", "React", "SCSS", "Stripe"],
    liveUrl: "https://www.templatevaults.com/",
    repositoryUrl: null,
    cover: {
      src: "/images/projects/templatevaults.webp",
      width: 1440,
      height: 1000,
      alt: "TemplateVaults’ live storefront homepage with its website-template headline and Browse Templates action",
      caption: "Live website / Template storefront",
    },
    views: [
      {
        src: "/images/projects/templatevaults-savory.webp",
        width: 1200,
        height: 570,
        alt: "Savory Eats Pro restaurant template preview from the TemplateVaults catalog",
        caption: "01 / Savory Eats Pro · Catalog preview",
      },
      {
        src: "/images/projects/templatevaults-bentofolio.webp",
        width: 1200,
        height: 577,
        alt: "Bentofolio developer portfolio template preview from the TemplateVaults catalog",
        caption: "02 / Bentofolio · Catalog preview",
      },
    ],
    overview:
      "TemplateVaults brings ready-made templates and custom development services together. I built the storefront so visitors could browse designs, check what is included, and try a demo before choosing a template.",
    challenge:
      "A screenshot only tells part of the story. I needed to make the differences between templates easy to compare and explain what happens after someone buys one.",
    goals: [
      "Let visitors compare templates by category and included features.",
      "Give each template a visual preview and a live demo.",
      "Connect discovery, checkout, and delivery in one product journey.",
    ],
    audience:
      "Small-business owners, freelancers, and creators who want a starting point for a website or help with a custom build.",
    responsibilities: [
      "Storefront and product-page design",
      "Catalog filtering and template previews",
      "Checkout and download integration",
    ],
    direction:
      "The storefront uses a dark background and bright accents, while the template previews bring in their own colors and styles. Features and categories sit close to each preview so they are easy to compare.",
    interaction:
      "Category controls filter the collection without leaving the page. Each template leads to more detail and a separate demo, while documentation and custom-project pages offer a next step for people who need more help.",
    technical:
      "Next.js and React organize the storefront and product routes, with SCSS for styling. Server routes integrate Stripe checkout and session checks, downloadable ZIP packages, license text, and email delivery.",
    responsive:
      "The catalog and product layouts adapt from larger grids to a single column. Template images keep their proportions, and feature lists sit alongside the decision they help a visitor make.",
    accessibility:
      "Readable descriptions and text-based category controls keep the catalog understandable alongside the previews. A further keyboard and screen-reader review of the full checkout and download journey is part of the next iteration.",
    performance:
      "Filtering works on the local catalog rather than making a request for each category change. Preview image sizes and third-party requests are the main areas to watch as the collection grows.",
    solution:
      "I worked through the whole purchase path, including the less visible parts: license information, documentation, payment checks, and the download. Those details affect whether someone can actually get started with the template.",
    outcome:
      "A live TemplateVaults storefront with restaurant and portfolio templates, product detail pages, published previews, and supporting customization documentation.",
    lessons:
      "The download is not the end of the experience. Clear setup instructions matter when someone opens the files and tries to make their first change.",
    nextSteps:
      "Add more templates, keep the catalog and product details in sync, and test what happens when a payment or download is interrupted.",
  },
  {
    slug: "tanis-portfolio",
    number: "04",
    title: "Tanis’s Portfolio",
    category: "Journalism & photography portfolio",
    type: "Professional portfolio · Work in progress",
    status: "In development",
    year: "2026",
    description:
      "A portfolio for Tanis Chavez’s sports reporting, photography, and on-camera work. Still in development.",
    role: "Design & full-stack development",
    technologies: ["PHP", "MySQL", "JavaScript", "CSS"],
    liveUrl: null,
    repositoryUrl: null,
    cover: {
      src: "/images/projects/tanis-highlight.webp",
      width: 1280,
      height: 720,
      alt: "Tanis Chavez holding a microphone in front of track-and-field posters, from her highlight reel thumbnail",
      caption: "Highlight reel / Portfolio in progress",
    },
    views: [
      {
        src: "/images/projects/tanis-highlight.webp",
        width: 1280,
        height: 720,
        alt: "The thumbnail of Tanis Chavez’s highlight reel, featured in her portfolio project",
        caption: "01 / Highlight reel thumbnail · The project’s featured media",
      },
    ],
    overview:
      "I’m building a portfolio for Tanis Chavez’s journalism, photography, interviews, and podcasts. Her highlight reel is one of the first things visitors see, followed by ways to explore the rest of her work.",
    challenge:
      "The site needs to bring several kinds of work together without burying the strongest pieces. Tanis also needs a way to upload and organize photos without asking me to edit the code each time.",
    goals: [
      "Put the highlight reel within easy reach.",
      "Organize photography and published work into clear sections.",
      "Make photo and category updates possible without editing code.",
    ],
    audience:
      "Editors, producers, prospective employers, and collaborators reviewing Tanis’s reporting and visual work.",
    responsibilities: [
      "Portfolio structure and visual direction",
      "Responsive public pages and media presentation",
      "Photo-management admin and database integration",
    ],
    direction:
      "I’m using soft colors and simple layouts around the reel and photography. Writing, interviews, and podcasts have their own sections so visitors can go straight to the work they want to see.",
    interaction:
      "The homepage previews the reel and featured photos. The gallery supports category browsing and a larger lightbox view; a private admin area provides photo uploads, captions, categories, and featured-image controls.",
    technical:
      "PHP templates and MySQL handle the pages and media records, with JavaScript for navigation, the gallery lightbox, and theme selection. CSS variables support peach, lavender, and mint themes, and the visitor’s choice persists locally.",
    responsive:
      "The page layout includes a mobile navigation state, responsive photo grids, and a video container that preserves the reel’s aspect ratio. The full collection still needs testing with final content on physical devices.",
    accessibility:
      "Video embeds have descriptive titles, gallery images use content captions, and Escape closes the lightbox. Keyboard focus management, media captions, and the completed content remain part of the pre-launch review.",
    performance:
      "Photos and embedded media load lazily. Before launch, the image upload workflow needs appropriately sized thumbnails so a growing gallery does not send full-resolution photographs to every visitor.",
    solution:
      "I’m building the public pages and the photo admin together. When Tanis uploads a photo, its caption, category, and featured status need to carry through to the right place on the site.",
    outcome:
      "The public pages, reel embed, gallery, and photo admin are in development. The site is not live yet; final content and launch checks are still to come.",
    lessons:
      "I’ve had to think about the person updating the portfolio as much as the person reading it. A simple upload process makes it easier to keep the public site current.",
    nextSteps:
      "Add the final photos and published pieces, finish testing the admin and keyboard navigation, and prepare the site for launch.",
  },
];
